import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { PlusIcon } from '@phosphor-icons/react'

import { Button, Divider, Text, ToastContext } from '@reveni-io/reveni-ui'

import LoadMore from '@/common/components/LoadMore/LoadMore'
import QueryFilter from '@/common/components/QueryFilter/QueryFilter'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useDragSort from '@/common/hooks/useDragSort/useDragSort'
import useShowMore from '@/common/hooks/useShowMore/useShowMore'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import DeleteVisibilityCriterionModal from './DeleteVisibilityCriterionModal/DeleteVisibilityCriterionModal'
import useVisibilityCriteria from './hooks/useVisibilityCriteria/useVisibilityCriteria'
import VisibilityCriterionItemList from './VisibilityCriterionItemList/VisibilityCriterionItemList'
import * as Styles from './styles'

const VisibilityCriteriaTab = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const {
    visibilityCriteria,
    gettingVisibilityCriteria,
    search,
    setSearch,
    loadMore,
    hasNextPage,
    totalCount,
    navigateToDetail,
    navigateToDuplicate,
    navigateToCreate,
    reorderCriteria,
    hasChanges,
    revertChanges,
    onSave,
    reorderingVisibilityCriteria,
    deletingVisibilityCriterion,
    criterionToDelete,
    setCriterionToDelete,
    confirmDeleteCriterion,
    cancelDeleteCriterion,
  } = useVisibilityCriteria({ storeId: info?.selectedStore?.id, setToastInfo })

  const { elementsToShow, hasMoreElements, showMore, totalElements, numberOfElementsToShow } = useShowMore({
    elements: visibilityCriteria,
    paginated: true,
    hasNextPage,
    onLoadMore: loadMore,
    totalCount,
  })

  const { dragging, onSortStart, onSortEnd } = useDragSort(reorderCriteria)
  const isEditable = hasPermission(userPermissions.editOrderTrackingPage)
  const isEmpty = !gettingVisibilityCriteria && totalCount === 0 && !search

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
      <Text type="bodyMRegular">
        {t('Añade condiciones para controlar qué bloques son visibles para determinados clientes')}
      </Text>
      {!isEmpty && (
        <>
          <QueryFilter onChange={setSearch} placeholder={t('Id, Nombre')} />
          <Divider />
        </>
      )}
      <VisibilityCriterionItemList
        isEditable={isEditable}
        criteria={elementsToShow}
        loading={gettingVisibilityCriteria}
        onEdit={navigateToDetail}
        onDuplicate={navigateToDuplicate}
        onDelete={setCriterionToDelete}
        dragging={dragging}
        onSortStart={onSortStart}
        onSortEnd={onSortEnd}
      />
      {!isEmpty && (
        <>
          <Divider />
          <LoadMore
            numberOfElementsToShow={numberOfElementsToShow}
            totalElements={totalElements}
            hasMoreElements={hasMoreElements}
            showMore={showMore}
          />
        </>
      )}
      {isEditable && (
        <div>
          <Button variant="text" size="s" onClick={navigateToCreate}>
            <PlusIcon size={24} />
            {t('Añadir nuevo criterio de visibilidad')}
          </Button>
        </div>
      )}
      <SaveChangesButtons
        hasChanges={isEditable && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={reorderingVisibilityCriteria}
        disabled={reorderingVisibilityCriteria}
      />
      <DeleteVisibilityCriterionModal
        show={!!criterionToDelete}
        onClose={cancelDeleteCriterion}
        onDelete={confirmDeleteCriterion}
        loading={deletingVisibilityCriterion}
      />
    </Styles.MainContainer>
  )
}

export default VisibilityCriteriaTab
